import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { X, ExternalLink, ChevronLeft, ChevronRight, Play, Pause, Volume2, VolumeX } from 'lucide-react';
import api from '../../lib/axios';
import { cn } from '../../lib/utils';

// =============================================
// HEADER BANNER - Bandeau carrousel amélioré
// Supporte: images de fond, vidéos, overlay, boutons d'action
// Taille recommandée: 1920x120px (image) ou 1920x120px (vidéo)
// =============================================

const HeaderBanner = () => {
  const { user, isClient, isAgency, isTeamMember } = useAuth();
  const [announcements, setAnnouncements] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [dismissed, setDismissed] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isPaused, setIsPaused] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const videoRef = useRef(null);

  useEffect(() => {
    if (user) {
      fetchAnnouncements();
      const savedDismissed = JSON.parse(localStorage.getItem('dismissedHeaderAnnouncements') || '[]');
      setDismissed(savedDismissed);
    } else {
      setLoading(false);
    }
  }, [user]);

  // Auto-rotate announcements every 6 seconds
  useEffect(() => {
    if (announcements.length > 1 && !isPaused) {
      const interval = setInterval(() => {
        setCurrentIndex((prev) => (prev + 1) % announcements.length);
      }, 6000);
      return () => clearInterval(interval);
    }
  }, [announcements.length, isPaused]);

  // Handle video autoplay when slide changes
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.currentTime = 0;
      videoRef.current.play().catch(() => {});
    }
  }, [currentIndex]);

  const fetchAnnouncements = async () => {
    try {
      let audience = 'all';
      if (isClient) audience = 'clients';
      else if (isTeamMember) audience = 'members';
      
      const response = await api.get('/settings/announcements', {
        params: {
          display_type: 'header',
          role: audience
        }
      });
      
      const savedDismissed = JSON.parse(localStorage.getItem('dismissedHeaderAnnouncements') || '[]');
      const activeAnnouncements = (response.data || []).filter(
        (a) => !savedDismissed.includes(a.id)
      );
      
      setAnnouncements(activeAnnouncements);
    } catch (error) {
      console.error('Error fetching header announcements:', error);
      setAnnouncements([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDismiss = (announcementId) => {
    const newDismissed = [...dismissed, announcementId];
    setDismissed(newDismissed);
    localStorage.setItem('dismissedHeaderAnnouncements', JSON.stringify(newDismissed));
    
    const remaining = announcements.filter((a) => a.id !== announcementId);
    setAnnouncements(remaining);
    
    if (currentIndex >= remaining.length && remaining.length > 0) {
      setCurrentIndex(0);
    }
    
    api.post(`/settings/announcements/${announcementId}/dismiss`).catch(() => {});
  };

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev - 1 + announcements.length) % announcements.length);
  };

  const goToNext = () => {
    setCurrentIndex((prev) => (prev + 1) % announcements.length);
  };

  const toggleMute = (e) => {
    e.stopPropagation();
    setIsMuted(!isMuted);
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
    }
  };

  if (loading || announcements.length === 0 || !user) {
    return null;
  }

  const currentAnnouncement = announcements[currentIndex];
  
  // Media configuration
  const hasBackgroundImage = currentAnnouncement.media_type === 'image' && currentAnnouncement.media_url;
  const hasBackgroundVideo = currentAnnouncement.media_type === 'video' && currentAnnouncement.media_url;
  const hasMedia = hasBackgroundImage || hasBackgroundVideo;
  
  // Colors and overlay
  const bgColor = currentAnnouncement.background_color || '#1a365d';
  const textColor = currentAnnouncement.text_color || '#ffffff';
  const overlayColor = currentAnnouncement.overlay_color || bgColor;
  const overlayOpacity = currentAnnouncement.overlay_opacity ?? 0.6;
  
  // Banner height - compact style for header
  const bannerHeight = currentAnnouncement.banner_height || 'compact'; // compact, medium, large
  const heightClass = {
    compact: 'h-12 md:h-14',
    medium: 'h-20 md:h-24',
    large: 'h-32 md:h-40'
  }[bannerHeight] || 'h-12 md:h-14';

  return (
    <div
      className={cn(
        "w-full relative overflow-hidden transition-all duration-500",
        heightClass
      )}
      style={{ 
        backgroundColor: !hasMedia ? bgColor : 'transparent',
        color: textColor 
      }}
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      data-testid="header-banner"
    >
      {/* Background Media */}
      {hasBackgroundImage && (
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ 
            backgroundImage: `url(${currentAnnouncement.media_url})`,
          }}
        />
      )}
      
      {hasBackgroundVideo && (
        <video
          ref={videoRef}
          className="absolute inset-0 w-full h-full object-cover"
          src={currentAnnouncement.media_url}
          autoPlay
          loop
          muted={isMuted}
          playsInline
        />
      )}
      
      {/* Color Overlay */}
      {hasMedia && (
        <div 
          className="absolute inset-0"
          style={{ 
            backgroundColor: overlayColor,
            opacity: overlayOpacity
          }}
        />
      )}
      
      {/* No media placeholder - shows expected size */}
      {!hasMedia && !currentAnnouncement.background_color && (
        <div className="absolute inset-0 bg-slate-100 flex items-center justify-center">
          <span className="text-slate-400 text-xs">
            Image recommandée: 1920 × {bannerHeight === 'compact' ? '56' : bannerHeight === 'medium' ? '96' : '160'}px
          </span>
        </div>
      )}

      {/* Content */}
      <div className="relative z-10 h-full max-w-7xl mx-auto px-4 flex items-center justify-between gap-3">
        {/* Navigation arrows (if multiple announcements) */}
        {announcements.length > 1 && (
          <button
            onClick={goToPrevious}
            className="p-1.5 hover:bg-white/20 rounded-full transition-colors flex-shrink-0 backdrop-blur-sm"
            title="Précédent"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
        )}

        {/* Main Content */}
        <div className="flex-1 flex items-center justify-center gap-4 min-w-0">
          {/* Title & Content */}
          <div className="text-center md:text-left flex-1 min-w-0">
            <h3 className="font-bold text-sm md:text-base truncate drop-shadow-sm">
              {currentAnnouncement.title}
            </h3>
            {currentAnnouncement.content && bannerHeight !== 'compact' && (
              <p className="text-xs md:text-sm opacity-90 truncate mt-0.5 drop-shadow-sm">
                {currentAnnouncement.content}
              </p>
            )}
            {currentAnnouncement.content && bannerHeight === 'compact' && (
              <span className="text-xs opacity-90 hidden md:inline ml-2">
                — {currentAnnouncement.content}
              </span>
            )}
          </div>
          
          {/* Action Button */}
          {currentAnnouncement.action_button_text && currentAnnouncement.action_button_url && (
            <a
              href={currentAnnouncement.action_button_url}
              target="_blank"
              rel="noopener noreferrer"
              className={cn(
                "flex-shrink-0 flex items-center gap-1.5 font-semibold transition-all",
                "bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-full",
                bannerHeight === 'compact' 
                  ? "px-3 py-1 text-xs" 
                  : "px-4 py-2 text-sm"
              )}
              style={{ color: textColor }}
            >
              {currentAnnouncement.action_button_text}
              <ExternalLink className="w-3 h-3" />
            </a>
          )}
        </div>

        {/* Right side controls */}
        <div className="flex items-center gap-2 flex-shrink-0">
          {/* Video mute toggle */}
          {hasBackgroundVideo && (
            <button
              onClick={toggleMute}
              className="p-1.5 hover:bg-white/20 rounded-full transition-colors backdrop-blur-sm"
              title={isMuted ? "Activer le son" : "Couper le son"}
            >
              {isMuted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
            </button>
          )}
          
          {/* Pagination dots */}
          {announcements.length > 1 && (
            <div className="flex gap-1.5 mx-2">
              {announcements.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrentIndex(idx)}
                  className={cn(
                    "w-2 h-2 rounded-full transition-all",
                    idx === currentIndex 
                      ? "bg-white scale-125" 
                      : "bg-white/40 hover:bg-white/60"
                  )}
                  title={`Annonce ${idx + 1}`}
                />
              ))}
            </div>
          )}
          
          {/* Next arrow */}
          {announcements.length > 1 && (
            <button
              onClick={goToNext}
              className="p-1.5 hover:bg-white/20 rounded-full transition-colors backdrop-blur-sm"
              title="Suivant"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          )}

          {/* Close button */}
          <button
            onClick={() => handleDismiss(currentAnnouncement.id)}
            className="p-1.5 hover:bg-white/20 rounded-full transition-colors backdrop-blur-sm"
            title="Fermer cette annonce"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
      
      {/* Progress bar for auto-rotation */}
      {announcements.length > 1 && !isPaused && (
        <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-white/20">
          <div 
            className="h-full bg-white/60 transition-all"
            style={{ 
              width: '100%',
              animation: 'progress 6s linear infinite'
            }}
          />
        </div>
      )}
      
      <style>{`
        @keyframes progress {
          from { width: 0%; }
          to { width: 100%; }
        }
      `}</style>
    </div>
  );
};

export default HeaderBanner;
